import {
  Bell,
  BrainCircuit,
  CalendarClock,
  ChevronDown,
  ChevronsUpDown,
  Download,
  ExternalLink,
  Inbox,
  LayoutGrid,
  Lightbulb,
  MessagesSquare,
  MonitorCheck,
  Radar,
  RadioTower,
  Settings2,
  Star,
  UserRound,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import type { VendorPage } from "@/lib/api";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useVendorPages } from "@/hooks/useVendorPages";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

/** App-wide sidebar — matches the shadcn dashboard example pattern:
 *
 *  - Header: brand block (SidebarMenuButton size="lg").
 *  - Content: two SidebarGroups
 *      1. Navigation — Alerts, Tools, Chat rooms
 *      2. Admin     — Sources, Settings
 *  - Footer: user-profile-style SidebarMenuButton that opens a
 *    DropdownMenu (side="right", align="end") — canonical shadcn
 *    footer, not a drawer/sheet. Operator actions (install app, theme,
 *    future auth/logout) live here.
 */
export function AppSidebar() {
  const location = useLocation();
  const { canInstall, install } = useInstallPrompt();
  const toolsQ = useVendorPages({ page_type: "tool" });
  const roomsQ = useVendorPages({ page_type: "chat_room" });
  const tools = toolsQ.data ?? [];
  const rooms = roomsQ.data ?? [];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-1">
          <SidebarMenu className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <NavLink to="/inbox">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <Bell className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">signals</span>
                    <span className="truncate text-xs">signal inbox</span>
                  </div>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarTrigger className="shrink-0" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavMenuItem
                to="/inbox"
                label="Signal Inbox"
                icon={Inbox}
                active={location.pathname.startsWith("/inbox")}
              />
              <NavMenuItem
                to="/candidates"
                label="Candidates"
                icon={Lightbulb}
                active={location.pathname.startsWith("/candidates")}
              />
              <SidebarMenuItem>
                <CollapsibleMenuButton
                  icon={Wrench}
                  label="Tools"
                  tooltip="Tools"
                  isActive={location.pathname.startsWith("/tools")}
                  defaultOpen={location.pathname.startsWith("/tools")}
                >
                  <ToolsSubMenu tools={tools} currentPath={location.pathname} />
                </CollapsibleMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <CollapsibleMenuButton
                  icon={MessagesSquare}
                  label="Chat rooms"
                  tooltip="Chat rooms"
                  isActive={location.pathname.startsWith("/chats")}
                  defaultOpen={location.pathname.startsWith("/chats")}
                >
                  <RoomsSubMenu rooms={rooms} currentPath={location.pathname} />
                </CollapsibleMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>TradeIdeas</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <CollapsibleMenuButton
                  icon={RadioTower}
                  label="TI Bot"
                  tooltip="TradeIdeas"
                  isActive={location.pathname.startsWith("/tradeideas")}
                  defaultOpen={location.pathname.startsWith("/tradeideas")}
                >
                  <TradeIdeasSubMenu currentPath={location.pathname} />
                </CollapsibleMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <UserRound className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">Operator</span>
                    <span className="truncate text-xs text-muted-fg">
                      single-user
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-64 rounded-lg border-border/60 bg-popover/95 p-2 shadow-xl shadow-black/30 backdrop-blur"
                side="right"
                align="end"
                sideOffset={8}
              >
                <DropdownMenuLabel className="px-2.5 pb-2 pt-1 text-xs font-medium uppercase tracking-wider text-muted-fg">
                  Operator
                </DropdownMenuLabel>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/status">
                    <MonitorCheck className="size-4 shrink-0 text-muted-fg" />
                    <span>Operator Status</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/scraper">
                    <Radar className="size-4 shrink-0 text-muted-fg" />
                    <span>Scraper</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/webhooks">
                    <RadioTower className="size-4 shrink-0 text-muted-fg" />
                    <span>Webhooks</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/processor">
                    <BrainCircuit className="size-4 shrink-0 text-muted-fg" />
                    <span>Processor</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/publish">
                    <RadioTower className="size-4 shrink-0 text-muted-fg" />
                    <span>Publish</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/operator/schedules">
                    <CalendarClock className="size-4 shrink-0 text-muted-fg" />
                    <span>Scrape Runtime</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="mx-1 my-2 bg-border/50" />
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/sources">
                    <LayoutGrid className="size-4 shrink-0 text-muted-fg" />
                    <span>Sources</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/admin/sources">
                    <LayoutGrid className="size-4 shrink-0 text-muted-fg" />
                    <span>Source registry</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <NavLink to="/admin/vendor-pages">
                    <Wrench className="size-4 shrink-0 text-muted-fg" />
                    <span>Vendor pages</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <Settings2 className="size-4 shrink-0" />
                  <span>Settings · coming soon</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="mx-1 my-2 bg-border/50" />
                {canInstall ? (
                  <DropdownMenuItem
                    onClick={install}
                    className="gap-3 rounded-md px-2.5 py-2 text-sm"
                  >
                    <Download className="size-4 shrink-0 text-muted-fg" />
                    <span>Install app</span>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem disabled className="gap-3 rounded-md px-2.5 py-2 text-sm">
                  <Settings2 className="size-4 shrink-0" />
                  <span>Theme · coming soon</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function TradeIdeasSubMenu({ currentPath }: { currentPath: string }) {
  const items = [
    { to: "/tradeideas/status", label: "Status" },
    { to: "/tradeideas/scanners", label: "Scanners" },
    { to: "/tradeideas/filters", label: "Filters" },
    { to: "/tradeideas/delivery", label: "Delivery" },
  ];
  return (
    <SidebarMenuSub>
      {items.map((item) => (
        <SidebarMenuSubItem key={item.to}>
          <SidebarMenuSubButton asChild isActive={currentPath === item.to}>
            <NavLink to={item.to}>
              <span className="truncate">{item.label}</span>
            </NavLink>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ))}
    </SidebarMenuSub>
  );
}

function NavMenuItem({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string;
  label: string;
  icon: React.ElementType;
  active: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={label} isActive={active}>
        <NavLink to={to}>
          <Icon />
          <span>{label}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function CollapsibleMenuButton({
  icon: Icon,
  label,
  tooltip,
  isActive = false,
  defaultOpen,
  children,
}: {
  icon: React.ElementType;
  label: string;
  tooltip: string;
  isActive?: boolean;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  return (
    <>
      <SidebarMenuButton
        type="button"
        tooltip={tooltip}
        isActive={isActive}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
      <SidebarMenuAction
        type="button"
        aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <ChevronDown
          className={`transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </SidebarMenuAction>
      {open ? children : null}
    </>
  );
}

/** Tools sub-menu: favorites float to the top, remaining tools grouped
 *  by vendor with the vendor label as a dim prefix row.
 *
 *  Keeps a flat <SidebarMenuSub> — we don't nest <SidebarMenuSub>
 *  inside itself because shadcn's 2-level indent gets visually tight
 *  and hurts legibility. The vendor row is styled as a section header
 *  (small caps, no hover), not a clickable entry.
 */
function ToolsSubMenu({
  tools,
  currentPath,
}: {
  tools: VendorPage[];
  currentPath: string;
}) {
  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={currentPath === "/tools"}>
          <NavLink to="/tools">
            <span className="truncate">All tools</span>
          </NavLink>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      {tools.map((t) => (
        <ToolEntry key={t.id} tool={t} currentPath={currentPath} />
      ))}
    </SidebarMenuSub>
  );
}

function ToolEntry({
  tool,
  currentPath,
}: {
  tool: VendorPage;
  currentPath: string;
}) {
  const isInApp =
    ((tool.access_mode ?? "external") === "iframe" ||
      tool.access_mode === "scripted_iframe") &&
    tool.iframe_url;
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        isActive={isInApp ? currentPath === `/tools/${tool.id}` : false}
      >
        {isInApp ? (
          <NavLink to={`/tools/${tool.id}`}>
            <span className="truncate">{displayLabel(tool)}</span>
          </NavLink>
        ) : (
          <a
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            title={tool.notes ?? undefined}
          >
            <span className="truncate">{displayLabel(tool)}</span>
            <ExternalLink className="ml-auto size-3 text-muted-fg/70" />
          </a>
        )}
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

function RoomsSubMenu({
  rooms,
  currentPath,
}: {
  rooms: VendorPage[];
  currentPath: string;
}) {
  return (
    <SidebarMenuSub>
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={currentPath === "/chats"}>
          <NavLink to="/chats">
            <span className="truncate">All chat rooms</span>
          </NavLink>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
      {rooms.map((room) => (
        <RoomEntry key={room.id} room={room} />
      ))}
    </SidebarMenuSub>
  );
}

function RoomEntry({ room }: { room: VendorPage }) {
  const chatUrl = room.focused_url;
  return (
    <SidebarMenuSubItem className="flex min-w-0 items-center gap-1">
      <SidebarMenuSubButton asChild className="flex-1">
        <a
          href={room.url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open full room"
        >
          {room.status === "active" ? (
            <Star className="size-3 shrink-0 text-amber-400/80" aria-hidden />
          ) : null}
          <span className="truncate">{displayLabel(room)}</span>
          <ExternalLink className="ml-auto size-3 text-muted-fg/70" />
        </a>
      </SidebarMenuSubButton>
      {chatUrl ? (
        <button
          type="button"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-fg outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          title="Open chat in small window"
          onClick={() => openChatWindow(room)}
        >
          <MessagesSquare className="size-3.5" aria-hidden />
          <span className="sr-only">Open {displayLabel(room)} chat window</span>
        </button>
      ) : null}
    </SidebarMenuSubItem>
  );
}

function displayLabel(vendorPage: VendorPage): string {
  return vendorPage.nav_label ?? vendorPage.label;
}

function openChatWindow(room: VendorPage): void {
  if (!room.focused_url) return;
  window.open(
    room.focused_url,
    `signals-chat-${room.id}`,
    "popup=yes,width=520,height=760,resizable=yes,scrollbars=yes",
  );
}
